import { Link } from "@tanstack/react-router";
import { formatearMoneda, type EstadoProyecto } from "@uirtus/shared";
import { useState } from "react";
import { FormularioProyecto } from "../componentes/formulario-proyecto";
import { Aviso, Boton, Entrada, Etiqueta, Modal, Seleccion } from "../componentes/ui";
import { ErrorApi } from "../lib/api";
import { useCrearProyecto, useProyectos } from "../lib/proyectos";
import { useDebounce } from "../lib/use-debounce";

const ETIQUETA_ESTADO: Record<EstadoProyecto, string> = {
  PROPUESTA: "Propuesta",
  ACTIVO: "Activo",
  EN_PAUSA: "En pausa",
  COMPLETADO: "Completado",
  CANCELADO: "Cancelado",
};

const TONO_ESTADO: Record<EstadoProyecto, "neutro" | "exito" | "alerta" | "peligro"> = {
  PROPUESTA: "neutro",
  ACTIVO: "exito",
  EN_PAUSA: "alerta",
  COMPLETADO: "neutro",
  CANCELADO: "peligro",
};

export function Proyectos() {
  const [texto, setTexto] = useState("");
  const [estado, setEstado] = useState<EstadoProyecto | "">("");
  const [creando, setCreando] = useState(false);
  const busqueda = useDebounce(texto, 300);

  const { data, isLoading, isError, error } = useProyectos({
    q: busqueda || undefined,
    estado: estado || undefined,
  });

  const crear = useCrearProyecto();
  const proyectos = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Proyectos</h1>
          <p className="text-sm text-slate-500">Agrupa cotizaciones y facturas de un mismo trabajo.</p>
        </div>
        <Boton onClick={() => setCreando(true)}>Nuevo proyecto</Boton>
      </div>

      <div className="flex flex-wrap gap-2">
        <Entrada
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Buscar por clave, nombre o descripción"
          className="w-full sm:w-96"
        />
        <Seleccion
          value={estado}
          onChange={(e) => setEstado(e.target.value as EstadoProyecto | "")}
          className="w-full sm:w-44"
        >
          <option value="">Todos los estados</option>
          {Object.entries(ETIQUETA_ESTADO).map(([clave, texto]) => (
            <option key={clave} value={clave}>
              {texto}
            </option>
          ))}
        </Seleccion>
      </div>

      {isError && (
        <Aviso>
          {error instanceof ErrorApi ? error.message : "No se pudo cargar la lista"}
        </Aviso>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : isError ? null : proyectos.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 px-4 py-12 text-center dark:border-slate-700">
          <p className="text-sm text-slate-500">
            {busqueda || estado
              ? "Ningún proyecto coincide con los filtros."
              : "Todavía no hay proyectos."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800">
              <tr>
                <th className="px-4 py-2">Proyecto</th>
                <th className="px-4 py-2">Cliente</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2 text-right">Monto estimado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {proyectos.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-4 py-2">
                    <Link
                      to="/proyectos/$id"
                      params={{ id: p.id }}
                      className="font-medium text-marca-600 hover:underline dark:text-marca-500"
                    >
                      {p.nombre}
                    </Link>
                    {p.clave && (
                      <div className="font-mono text-xs text-slate-500">{p.clave}</div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div>{p.cliente.razonSocial}</div>
                    <div className="font-mono text-xs text-slate-500">{p.cliente.rfc}</div>
                  </td>
                  <td className="px-4 py-2">
                    <Etiqueta tono={TONO_ESTADO[p.estado]}>{ETIQUETA_ESTADO[p.estado]}</Etiqueta>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {p.montoEstimado ? formatearMoneda(p.montoEstimado, p.moneda) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        titulo="Nuevo proyecto"
        abierto={creando}
        alCerrar={() => {
          setCreando(false);
          crear.reset();
        }}
      >
        <FormularioProyecto
          enviando={crear.isPending}
          errorServidor={crear.error ? crear.error.message : null}
          alEnviar={(datos) =>
            crear.mutate(datos as never, {
              onSuccess: () => {
                setCreando(false);
                crear.reset();
              },
            })
          }
          alCancelar={() => {
            setCreando(false);
            crear.reset();
          }}
        />
      </Modal>
    </div>
  );
}
